class WctVariantUI extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    if (this.dataset.initialized) return;
    this.dataset.initialized = true;
    this.container = this.querySelector('.wct-custom-variant-radios');
    if (!this.container) return;

    const priceEl = this.container.querySelector('.wct-pkg-price');
    this.currencySymbol = priceEl ? priceEl.textContent.replace(/[\d\.,]/g, '') : '$';
    
    const jsonEl = this.container.querySelector('.wct-variants-json');
    if (!jsonEl) return;
    this.variantsData = JSON.parse(jsonEl.textContent);

    const metafieldsEl = this.container.querySelector('.wct-variant-metafields-json');
    this.variantMetafields = metafieldsEl ? JSON.parse(metafieldsEl.textContent) : {};

    this.bindEvents();
    this.updateVisuals(); 
  }

  formatMoney(cents) {
    return this.currencySymbol + (cents / 100).toFixed(2);
  }

  getDisplayCents(variant) {
    if (!variant) return 0;
    const meta = this.variantMetafields[variant.id] || this.variantMetafields[String(variant.id)];
    if (meta && meta.show_price != null && meta.show_price !== '') {
      const cents = parseInt(meta.show_price, 10);
      if (!isNaN(cents)) return cents;
    }
    return variant.price;
  }

  bindEvents() {
    this.container.addEventListener('change', (e) => {
      if (e.target.type === 'radio') {
        this.updateVisuals(e.target);
        this.container.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });

    this.container.addEventListener('click', (e) => {
      this.handleQuantity(e);
    });
  }

  handleQuantity(e) {
    const minusBtn = e.target.closest('.wct-qty-ctrl.minus');
    const plusBtn = e.target.closest('.wct-qty-ctrl.plus');
    
    if (minusBtn || plusBtn) {
      e.preventDefault();
      e.stopPropagation();
      
      const qtyContainer = e.target.closest('.wct-package-qty');
      const display = qtyContainer.querySelector('.wct-qty-display');
      let currentQty = parseInt(display.value) || 1;
      
      if (minusBtn && currentQty > 1) currentQty--;
      else if (plusBtn && currentQty < 99) currentQty++;
      
      display.value = currentQty;
      
      const masterQty = this.container.querySelector('.wct-hidden-master-qty');
      if (masterQty) {
        masterQty.value = currentQty;
        masterQty.dispatchEvent(new Event('change', { bubbles: true }));
      }
      this.updateMasterId();
    }
  }

  getPrimarySelections() {
    return Array.from(
      this.container.querySelectorAll('.wct-color-capsules input:checked, .wct-match-capsules input:checked')
    ).map(input => input.value);
  }

  isPrimaryFilterInput(input) {
    return input && (input.closest('.wct-color-label') || input.closest('.wct-match-label'));
  }

  updatePackageBadge(wrapper, matchedVariant) {
    const badgeEl = wrapper.querySelector('.wct-badge-custom');
    if (!badgeEl) return;

    const meta = matchedVariant && this.variantMetafields[matchedVariant.id]
      ? this.variantMetafields[matchedVariant.id]
      : null;
    const badge = meta ? meta.package_badge : '';
    const breathe = !!(meta && meta.breath_transition);

    badgeEl.classList.toggle('wct-badge-breathe', breathe);

    if (badge) {
      badgeEl.textContent = badge;
      badgeEl.style.display = '';
    } else {
      badgeEl.textContent = '';
      badgeEl.style.display = 'none';
    }
  }

  findVariantForPackage(pkgValue, primarySelections = this.getPrimarySelections()) {
    const candidates = primarySelections.length > 0
      ? this.variantsData.filter(v => primarySelections.every(selection => v.options.includes(selection)))
      : this.variantsData;

    return candidates.find(v => v.options.includes(pkgValue));
  }

  updatePackageCardState(wrapper, matchedVariant) {
    const input = wrapper.querySelector('input[type="radio"]');
    if (!input || !matchedVariant) return;

    input.dataset.variantId = matchedVariant.id;
    this.updatePackageBadge(wrapper, matchedVariant);

    const imgEl = wrapper.querySelector('.wct-package-img img');
    if (imgEl) {
      const newImgData = matchedVariant.featured_image || matchedVariant.featured_media?.preview_image;
      if (newImgData && newImgData.src) {
        imgEl.src = newImgData.src;
        imgEl.removeAttribute('srcset');
      }
    }

    const priceEl = wrapper.querySelector('.wct-pkg-price');
    const compareEl = wrapper.querySelector('.wct-pkg-compare');
    const saveEl = wrapper.querySelector('.wct-pkg-save');

    const displayCents = this.getDisplayCents(matchedVariant);
    if (priceEl) priceEl.textContent = this.formatMoney(displayCents);
    if (matchedVariant.compare_at_price > matchedVariant.price) {
      if (compareEl) {
        compareEl.textContent = this.formatMoney(matchedVariant.compare_at_price);
        compareEl.style.display = '';
      }
    } else if (compareEl) {
      compareEl.style.display = 'none';
    }
    if (saveEl) {
      if (matchedVariant.compare_at_price > displayCents) {
        saveEl.textContent = this.formatMoney(matchedVariant.compare_at_price - displayCents) + ' off';
        saveEl.style.display = '';
      } else {
        saveEl.style.display = 'none';
      }
    }

    if (matchedVariant.available) {
      wrapper.classList.remove('is-sold-out');
    } else {
      wrapper.classList.add('is-sold-out');
      if (input.checked) wrapper.classList.remove('is-active');
    }
  }

  updatePackageCards(targetInput) {
    const hasPrimaryFilters = this.container.querySelector('.wct-color-capsules, .wct-match-capsules');
    const primarySelections = this.getPrimarySelections();

    let validVariants = this.variantsData;
    if (hasPrimaryFilters && primarySelections.length > 0) {
      validVariants = this.variantsData.filter(v =>
        primarySelections.every(selection => v.options.includes(selection))
      );
    }

    let hasSelectedVisiblePackage = false;
    let firstAvailableInput = null;

    this.container.querySelectorAll('.wct-package-wrapper').forEach(wrapper => {
      const input = wrapper.querySelector('input[type="radio"]');
      if (!input) return;

      const pkgValue = input.value;
      let matchedVariant = null;

      if (hasPrimaryFilters) {
        if (primarySelections.length === 0) {
          wrapper.style.display = 'none';
          wrapper.classList.remove('is-active', 'is-sold-out');
          this.updatePackageBadge(wrapper, null);
          return;
        }
        matchedVariant = validVariants.find(v => v.options.includes(pkgValue));
      } else {
        matchedVariant = this.findVariantForPackage(pkgValue, primarySelections);
      }

      if (matchedVariant) {
        wrapper.style.display = 'block';
        this.updatePackageCardState(wrapper, matchedVariant);

        if (matchedVariant.available) {
          if (!firstAvailableInput) firstAvailableInput = input;
          if (input.checked) hasSelectedVisiblePackage = true;
        }
      } else if (hasPrimaryFilters) {
        wrapper.style.display = 'none';
        wrapper.classList.remove('is-active', 'is-sold-out');
        this.updatePackageBadge(wrapper, null);
      }
    });

    if (hasPrimaryFilters && !hasSelectedVisiblePackage && firstAvailableInput && targetInput && this.isPrimaryFilterInput(targetInput)) {
      firstAvailableInput.checked = true;
      firstAvailableInput.closest('.wct-package-wrapper').classList.add('is-active');
      const masterQty = this.container.querySelector('.wct-hidden-master-qty');
      if (masterQty) masterQty.value = 1;
    }
  }

  sortPackageCards() {
    const primarySelections = this.getPrimarySelections();

    this.container.querySelectorAll('.wct-package-cards').forEach(cardsContainer => {
      const wrappers = Array.from(cardsContainer.querySelectorAll('.wct-package-wrapper'));

      const getSortScore = (wrapper) => {
        if (wrapper.style.display === 'none') return 0;

        if (wrapper.classList.contains('is-sold-out')) return 1;

        const input = wrapper.querySelector('input[type="radio"]');
        if (!input) return 1;

        const validVariants = primarySelections.length > 0
          ? this.variantsData.filter(v => primarySelections.every(selection => v.options.includes(selection)))
          : this.variantsData;
        const matchedVariant = validVariants.find(v => v.options.includes(input.value));

        if (!matchedVariant) return 0;
        return matchedVariant.available ? 2 : 1;
      };

      wrappers
        .map((wrapper, index) => ({ wrapper, index, score: getSortScore(wrapper) }))
        .sort((a, b) => b.score - a.score || a.index - b.index)
        .forEach(({ wrapper }) => cardsContainer.appendChild(wrapper));
    });
  }

  updateVisuals(targetInput) {
    this.container.querySelectorAll('fieldset').forEach(fieldset => {
      fieldset.querySelectorAll('.is-active').forEach(el => el.classList.remove('is-active'));
      const checkedRadio = fieldset.querySelector('input[type="radio"]:checked');
      if (checkedRadio) {
        const wrapper = checkedRadio.closest('.wct-color-label, .wct-match-label, .wct-package-wrapper');
        if (wrapper) wrapper.classList.add('is-active');
      }
    });

    if (targetInput && targetInput.closest('.wct-package-wrapper')) {
      this.container.querySelectorAll('.wct-qty-display').forEach(input => input.value = 1);
      const masterQty = this.container.querySelector('.wct-hidden-master-qty');
      if (masterQty) {
        masterQty.value = 1;
      }
    }

    const hasPrimaryFilters = this.container.querySelector('.wct-color-capsules, .wct-match-capsules');
    if (!hasPrimaryFilters || this.getPrimarySelections().length > 0) {
      this.updatePackageCards(targetInput);
    }

    this.sortPackageCards();
    this.updateMasterId();
  }

  updateMasterId() {
    const selectedOptions = Array.from(this.container.querySelectorAll('input[type="radio"]:checked')).map(input => input.value);
    
    const currentVariant = this.variantsData.find(variant => {
      return selectedOptions.every(optionValue => variant.options.includes(optionValue));
    });

    if (currentVariant) {
      const masterIdInput = this.container.querySelector('.wct-hidden-master-id');
      if (masterIdInput) {
        masterIdInput.value = currentVariant.id;
      }

      const allIdInputs = document.querySelectorAll('input[name="id"]');
      allIdInputs.forEach(input => {
        input.value = currentVariant.id;
        input.disabled = false; 
        input.removeAttribute('disabled'); 
      });

      const mainATCButtons = document.querySelectorAll('button[name="add"], .add-to-cart, .product-form__submit, #AddToCart');
      mainATCButtons.forEach(btn => {
        if (currentVariant.available) {
            btn.disabled = false;
            btn.removeAttribute('disabled');
            btn.classList.remove('sold-out', 'disabled');
            btn.style.opacity = '1';
            btn.style.pointerEvents = 'auto';
        } else {
            btn.disabled = true;
            btn.classList.add('sold-out');
        }
        
        const textSpan = btn.querySelector('span, .single-add-to-cart-button--text');
        if (textSpan) {
          textSpan.textContent = currentVariant.available ? 'Add to cart' : 'Sold out';
        }
      });
      
      if (this.container.dataset.updateUrl === 'true') {
        window.history.replaceState({} , '', `${this.container.dataset.url}?variant=${currentVariant.id}`);
      }
    }

    this.broadcastSync(currentVariant);
  }

  broadcastSync(variant) {
    const activeVariantId = variant ? variant.id : this.container.querySelector('.wct-hidden-master-id')?.value;
    window.dispatchEvent(new CustomEvent('wct:variant:synced', {
      detail: {
        variantId: parseInt(activeVariantId),
        quantity: parseInt(this.container.querySelector('.wct-hidden-master-qty')?.value || 1)
      }
    }));
  }
}

if (!customElements.get('wct-variant-ui')) {
  customElements.define('wct-variant-ui', WctVariantUI);
}